import { ExternalLink, Phone, Reply } from "lucide-react";

import MarkdownWrapper from "../MarkdownWrapper";
import { cn } from "../../lib/utils";

// O template como o cliente viu: cabeçalho, corpo, rodapé e botões.
//
// Antes a conversa mostrava só o corpo — às vezes só "[template utilitario]".
// Quem atendia não sabia que botões o cliente tinha na frente, e por isso não
// entendia uma resposta como "Sim" vinda do nada.
//
// Os botões aparecem desenhados mas não clicáveis: quem toca neles é o
// cliente, no aparelho dele. Um botão clicável aqui prometeria uma ação que
// não existe deste lado.

const ROTULO_CATEGORIA = {
  MARKETING: "Marketing",
  UTILITY: "Utilitário",
  AUTHENTICATION: "Autenticação",
  SERVICE: "Atendimento"
};

const IconeDoBotao = ({ tipo }) => {
  if (tipo === "url") return <ExternalLink className="h-3 w-3" />;
  if (tipo === "phone_number") return <Phone className="h-3 w-3" />;

  return <Reply className="h-3 w-3" />;
};

const TemplateMessage = ({ dados, className, clicavel = false }) => {
  if (!dados) return null;

  const { cabecalho, corpo, rodape, botoes = [], nome, categoria } = dados;

  const handleCliqueBotao = (e, botao) => {
    if (!clicavel) return;
    if (!botao || !botao.texto) return;
    const tiposNaoTexto = ["url", "phone_number"];
    if (tiposNaoTexto.includes(botao.tipo)) return;
    e.preventDefault();
    e.stopPropagation();
    document.dispatchEvent(
      new CustomEvent("clicar-botao-recebido", {
        detail: { texto: botao.texto, skipSignature: true },
      }),
    );
  };

  return (
    <div
      className={cn(
        "overflow-hidden rounded-lg border bg-background/60",
        className,
      )}
    >
      {/* A etiqueta diz que aquilo não foi digitado por um atendente: saiu de
          um modelo aprovado pela Meta, e o nome é o que permite achar qual. */}
      {(nome || categoria) && (
        <div className="flex flex-wrap items-center gap-1.5 border-b bg-muted/50 px-2.5 py-1.5">
          <span className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
            Modelo
          </span>
          {nome && (
            <span className="font-mono text-[11px] text-muted-foreground">
              {nome}
            </span>
          )}
          {categoria && (
            <span className="rounded bg-background px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground">
              {ROTULO_CATEGORIA[String(categoria).toUpperCase()] || categoria}
            </span>
          )}
        </div>
      )}

      <div className="space-y-1.5 p-2.5">
        {cabecalho?.tipo === "image" && cabecalho.url && (
          <img
            src={cabecalho.url}
            alt=""
            className="max-h-48 w-full rounded-md bg-muted object-cover"
          />
        )}

        {cabecalho?.tipo === "video" && cabecalho.url && (
          <video
            src={cabecalho.url}
            controls
            className="max-h-48 w-full rounded-md bg-muted"
          />
        )}

        {cabecalho?.texto && (
          <p className="font-semibold leading-snug">{cabecalho.texto}</p>
        )}

        {corpo && (
          <div className="wrap-break-word text-sm">
            <MarkdownWrapper>{corpo}</MarkdownWrapper>
          </div>
        )}

        {rodape && (
          <p className="text-xs text-muted-foreground">{rodape}</p>
        )}
      </div>

      {botoes.length > 0 && (
        <div className="divide-y border-t">
          {botoes.map((botao, indice) => {
            const ehLink = botao.tipo === "url" && botao.url;
            const ehTelefone = botao.tipo === "phone_number" && botao.telefone;
            const ehTexto = botao.tipo !== "url" && botao.tipo !== "phone_number";
            const classesBase =
              "flex items-center justify-center gap-1.5 px-2.5 py-2 text-sm font-medium text-primary";
            const classesHover = clicavel && ehTexto
              ? "hover:bg-primary/5 transition-colors cursor-pointer active:bg-primary/10"
              : ehLink || ehTelefone
                ? "hover:bg-primary/5 transition-colors cursor-pointer active:bg-primary/10"
                : "";
            const tag = (clicavel && ehTexto) || ehLink || ehTelefone ? "button" : "div";
            const Tag = tag;
            return (
              <Tag
                key={`${botao.texto}-${indice}`}
                type={tag === "button" ? "button" : undefined}
                title={
                  botao.url
                    ? `Abre ${botao.url}`
                    : botao.telefone
                      ? `Liga para ${botao.telefone}`
                      : clicavel
                        ? "Clique para responder com este texto"
                        : "O cliente toca para responder"
                }
                disabled={tag === "button" ? false : undefined}
                onClick={(e) => {
                  if (ehLink && botao.url) {
                    e.preventDefault();
                    window.open(botao.url, "_blank", "noopener,noreferrer");
                    return;
                  }
                  if (ehTelefone && botao.telefone) {
                    e.preventDefault();
                    window.open(`tel:${botao.telefone}`, "_self");
                    return;
                  }
                  handleCliqueBotao(e, botao);
                }}
                className={cn(classesBase, classesHover)}
              >
                <IconeDoBotao tipo={botao.tipo} />
                {botao.texto}
              </Tag>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default TemplateMessage;
